import { useDispatch, useSelector } from "react-redux";
import {
  IAdhocActionResponse,
  IAdhocActionResponseNew,
  IPolicy,
  PolicyType,
  validatePolicySync,
  yupErrorMessageParse,
} from "shared";
import { useEffect, useMemo } from "react";
import { SerializedError } from "@reduxjs/toolkit";

import { RootState } from "../store";
import {
  IAdhocActionModal,
  initialState,
  reset,
  setState,
} from "../slices/adhocActionModal";
import { useLazyPreviewPolicyQuery, useUpdateSystemPolicyMutation } from "../services/api/policyApi/policy";
import {
  useAdhocInterestChargeMutation,
  useAdhocStatementMutation,
  useFetchContactQuery,
} from "../services/api/contactsApi/contact";
import {
  useAdhocInvoiceEmailMutation,
  useAdhocInvoiceSmsMutation,
  useAdhocLateFeeMutation,
} from "../services/api/invoiceApi/invoice";
import { useAdhocBulkActionsMutation } from "../services/api/actionApi/action";
import { statementTemplateChoiceFields } from "../components/Settings/Policies/PolicyForm/Fields/StatementTemplateSelector";

import { useSystemPolicies } from "./useSystemPolicies";
import { useGetSelectedOrganisation } from "./useGetSelectedOrganisation";

export interface IAdhocOpenOptions {
  /** Called once with the result after the user applies the action */
  onSuccess?: (result: IAdhocActionResponse | IAdhocActionResponseNew) => void;
  /** Bulk reminders: leave out frozen, queried and promised invoices (keeps a single invoice on the bulk path too) */
  checkChaseable?: boolean;
}

// Callbacks are not serialisable so they live beside the store; only one modal is open at a time
let onSuccessHandler: IAdhocOpenOptions[`onSuccess`];

function notifySuccess(result: IAdhocActionResponse | IAdhocActionResponseNew) {
  const handler = onSuccessHandler;

  onSuccessHandler = undefined;
  handler?.(result);
}

export function useAdhocActionModal() {
  const {
    type,
    show,
    currentPolicy,
    currentPolicyForm,
    contactId,
    contactPersonIds,
    result,
    resultNew,
    contactName,
    invoiceNumber,
    invoiceId,
    view,
    preview,
    previewError,
    previewLoading,
    applyLoading,
    updatePolicyLoading,
    isBulk,
    invoiceIds,
    contactIds,
    isAnnouncement,
    checkChaseable,
    invalids,
  } = useSelector((state: RootState) => state.adhocActionModal);
  const currentOrg = useGetSelectedOrganisation();
  const dispatch = useDispatch();
  const [updatePolicy, {
    isLoading: saveLoading,
  }] = useUpdateSystemPolicyMutation();
  const [adhocStatement, { isLoading: adhocStatementLoading }] = useAdhocStatementMutation();
  const [adhocInterestCharge, { isLoading: isChargingInterest }] = useAdhocInterestChargeMutation();
  const [adhocInvoiceEmail, { isLoading: isSendingInvoiceEmail }] = useAdhocInvoiceEmailMutation();
  const [adhocLateFee, { isLoading: isIssuingLateFee }] = useAdhocLateFeeMutation();
  const [adhoceInvoicSms, { isLoading: isSendingSms }] = useAdhocInvoiceSmsMutation();
  const [adhocBulkActions, { isLoading: isLoadingBulkActions }] = useAdhocBulkActionsMutation();

  const [previewApi, {
    isLoading: previewLoadingSrc,
    data: previewData,
    isFetching: previewFetching,
    error: previewApiError,
  }] = useLazyPreviewPolicyQuery();

  const { data: systemPolicies, isLoading: systemPolicesLoading } = useSystemPolicies();

  // For adhoc statements, load the contact so the user can pick which contact persons receive it.
  // currentData, not data: data keeps the previous contact while the next one loads, which would
  // seed contactPersonIds with the old contact's persons
  const { currentData: adhocStatementContact, isFetching: adhocContactLoading } = useFetchContactQuery({
    organisationId: currentOrg?.id as string,
    contactId: contactId as number,
  }, {
    skip: !show || isBulk || type !== PolicyType.STATEMENT || !contactId || !currentOrg?.id,
  });

  const adhocContactPersons = useMemo(() => {
    if (!adhocStatementContact) return [];

    const persons = [
      adhocStatementContact.primaryContactPerson,
      ...(adhocStatementContact.contactPersons || []),
    ].filter(Boolean);

    const seen = new Set<string>();

    return persons.filter(person => {
      if (!person.id || seen.has(person.id)) return false;

      seen.add(person.id);

      return true;
    });
  }, [adhocStatementContact]);

  const eligibleContactPersonIds = useMemo(() =>
    adhocContactPersons
      .filter(person => (person.isPrimary || person.includeInNotifications) && person.email)
      .map(person => person.id as string),
  [adhocContactPersons]);

  // Default to all eligible persons selected once the contact loads
  useEffect(() => {
    if (!show || isBulk || type !== PolicyType.STATEMENT) return;
    if (contactPersonIds !== undefined) return;
    if (!adhocStatementContact) return;

    dispatch(setState({ contactPersonIds: eligibleContactPersonIds }));
  }, [show, isBulk, type, contactPersonIds, adhocStatementContact, eligibleContactPersonIds]);

  function setContactPersonIds(ids?: string[]) {
    dispatch(setState({ contactPersonIds: ids }));
  }

  /** The subset of persons to send to, or undefined for the default (all) behaviour */
  const contactPersonIdsToSend = useMemo(() => {
    if (isBulk || type !== PolicyType.STATEMENT) return undefined;
    if (contactPersonIds === undefined || !eligibleContactPersonIds.length) return undefined;

    const isAllSelected = eligibleContactPersonIds.every(id => contactPersonIds.includes(id));

    return isAllSelected ? undefined : contactPersonIds;
  }, [isBulk, type, contactPersonIds, eligibleContactPersonIds]);

  /** No contact persons selected for an adhoc statement - block sending */
  const noContactPersonsSelected = !isBulk &&
    type === PolicyType.STATEMENT &&
    contactPersonIds !== undefined &&
    contactPersonIds.length === 0;

  const showPersonSelect = !isBulk &&
    !isAnnouncement &&
    (adhocContactPersons.length > 1 ||
      eligibleContactPersonIds.length === 0 ||
      noContactPersonsSelected);

  const close = () => {
    onSuccessHandler = undefined;
    dispatch(reset());
  };

  function openModal(state: Partial<IAdhocActionModal>, options?: IAdhocOpenOptions) {
    onSuccessHandler = options?.onSuccess;

    dispatch(setState({
      ...initialState,
      show: true,
      checkChaseable: !!options?.checkChaseable,
      ...state,
    }));
  }

  const openSendStatement = (contactId: number, contactName: string, options?: IAdhocOpenOptions) => {
    openModal({
      type: PolicyType.STATEMENT,
      show: true,
      contactId,
      contactName,
    }, options);
  };

  const openChargeInterest = (contactId: number, contactName: string) => {
    openModal({
      type: PolicyType.STATEMENT_LATE_FEE,
      show: true,
      contactId,
      contactName,
    });
  };

  const openSendInvoiceEmail = (invoiceId: string, invoiceNumber: string, options?: IAdhocOpenOptions) => {
    openModal({
      type: PolicyType.REMINDER,
      show: true,
      invoiceId,
      invoiceNumber,
    }, options);
  };

  const openIssueLateFee = (invoiceId: string, invoiceNumber: string) => {
    openModal({
      type: PolicyType.LATE_FEE,
      show: true,
      invoiceId,
      invoiceNumber,
    });
  };

  const openSendSms = (invoiceId: string, invoiceNumber: string) => {
    openModal({
      type: PolicyType.SMS,
      show: true,
      invoiceId,
      invoiceNumber,
    });
  };

  const openBulkLateFees = (invoiceIds: string[]) => {
    if (invoiceIds.length === 1) {
      return openIssueLateFee(invoiceIds[0], ``);
    }

    openModal({
      type: PolicyType.LATE_FEE,
      show: true,
      invoiceIds,
      isBulk: true,
    });
  };

  const openBulkReminders = (invoiceIds: string[], options?: IAdhocOpenOptions) => {
    if (invoiceIds.length === 1 && !options?.checkChaseable) {
      return openSendInvoiceEmail(invoiceIds[0], ``, options);
    }

    openModal({
      type: PolicyType.REMINDER,
      show: true,
      invoiceIds,
      isBulk: true,
    }, options);
  };

  const openBulkStatements = (contactIds: number[], options?: IAdhocOpenOptions) => {
    if (contactIds.length === 1) {
      return openSendStatement(contactIds[0], ``, options);
    }

    openModal({
      type: PolicyType.STATEMENT,
      show: true,
      contactIds,
      isBulk: true,
      isAnnouncement: false,
    }, options);
  };

  const openBulkInterests = (contactIds: number[]) => {
    if (contactIds.length === 1) {
      return openChargeInterest(contactIds[0], ``);
    }

    openModal({
      type: PolicyType.STATEMENT_LATE_FEE,
      show: true,
      contactIds,
      isBulk: true,
    });
  };

  const openBulkAnnouncements = (contactIds: number[]) => {
    openModal({
      type: PolicyType.STATEMENT, // todo: or reminder?
      show: true,
      contactIds,
      isBulk: true,
      isAnnouncement: true,
    });
  };

  useEffect(() => {
    if (systemPolicies && type && !currentPolicy) {
      const found = systemPolicies.find(policy => policy.policy_type === type);
      if (found) {
        dispatch(setState({
          currentPolicy: found,
          currentPolicyForm: { ...found },
        }));
      }
    }
  }, [systemPolicies, type, currentPolicy]);

  function update({ value, name }: { value: string | number | boolean; name: string }) {
    dispatch(setState({
      currentPolicyForm: {
        ...currentPolicyForm,
        // Organisation default / standard / a template is one dropdown but two stored fields
        ...(name === `statement_template_id` ? statementTemplateChoiceFields(value) : { [name]: value }),
      },
    }));
  }

  async function saveAndExecute() {
    if (!currentPolicyForm) return;
    if (!currentPolicy) return;
    if (!type) return;
    if (!show) return;

    dispatch(setState({
      view: `result`,
    }));

    // Save the policy
    const validated = validate({ ...currentPolicyForm });

    if (!validated) {
      console.error(`Validation failed`);

      return;
    }

    await updatePolicy({
      organisationId: currentPolicy.organisation_id,
      id: currentPolicy.id,
      policy: currentPolicyForm,
      groupIds: [],
    });

    if (isBulk) {
      let result: null | IAdhocActionResponseNew = null;

      const response = await adhocBulkActions({
        organisationId: currentOrg?.id,
        type,
        invoiceIds,
        contactIds,
        checkChaseable,
      });

      if (response.data) {
        result = response.data;
      }

      dispatch(setState({
        resultNew: result,
      }));

      if (result) notifySuccess(result);

      return;
    }

    let result: null | IAdhocActionResponse | IAdhocActionResponse[] = null;
    if (type === PolicyType.STATEMENT) {
      if (noContactPersonsSelected) return;

      const response = await adhocStatement({
        organisationId: currentPolicy.organisation_id,
        contactId,
        contactPersonIds: contactPersonIdsToSend,
      });

      if (response.data?.action) {
        result = response.data;
      }
    }

    if (type === PolicyType.STATEMENT_LATE_FEE) {
      const response = await adhocInterestCharge({
        organisationId: currentOrg?.id,
        contactId,
      });

      if (response.data?.action) {
        result = response.data;
      }
    }

    if (type === PolicyType.REMINDER) {
      const response = await adhocInvoiceEmail({
        organisationId: currentOrg?.id,
        invoiceId,
      });

      if (response.data?.action) {
        result = response.data;
      }
    }

    if (type === PolicyType.LATE_FEE) {
      const response = await adhocLateFee({
        organisationId: currentOrg?.id,
        invoiceId,
      });

      if (response.data?.action) {
        result = response.data;
      }
    }

    if (type === PolicyType.SMS) {
      const response = await adhoceInvoicSms({
        organisationId: currentOrg?.id,
        invoiceId,
      });

      if (response.data?.action) {
        result = response.data;
      }
    }

    dispatch(setState({
      result,
    }));

    if (result) notifySuccess(result as IAdhocActionResponse);
  }

  async function onPreview() {
    if (!currentPolicyForm) return;
    if (!currentPolicy) return;
    if (!type) return;
    if (!show) return;

    dispatch(setState({
      view: `preview`,
      previewError: undefined,
      preview: undefined,
    }));

    // Save the policy
    const validated = validate({ ...currentPolicyForm });

    if (!validated) {
      return;
    }

    await updatePolicy({
      organisationId: currentPolicy.organisation_id,
      id: currentPolicy.id,
      policy: currentPolicyForm,
      groupIds: [],
    });

    // Preview renders a single email, so pick one selected person (prefer the primary)
    let previewContactPersonId: string | undefined;

    if (contactPersonIdsToSend?.length) {
      const primary = adhocContactPersons.find(person => person.isPrimary);

      previewContactPersonId = (primary?.id && contactPersonIdsToSend.includes(primary.id)) ?
        primary.id :
        contactPersonIdsToSend[0];
    }

    // Then call preview
    previewApi({
      organisationId: currentPolicy.organisation_id,
      policyId: currentPolicy.id,
      invoiceId,
      contactId,
      contactPersonId: previewContactPersonId,
      contactPersonIds: contactPersonIdsToSend,
    });
  }

  function getPreviewErrorMessage(error: {
    status?: number;
    data?: {
      message?: string;
    };
    error?: string;
  } | SerializedError | undefined) {
    if (!error) {
      return undefined;
    }

    if (`data` in error && error.data?.message) {
      return error.data.message;
    }

    if (`message` in error && error.message) {
      return error.message;
    }

    if (`error` in error && error.error) {
      return error.error;
    }

    return `Failed to generate preview`;
  }

  function validate(pol: IPolicy) {
    try {
      const valid = validatePolicySync(pol, false);

      dispatch(setState({ invalids: [] }));

      return valid;
    }
    catch (err) {
      console.error(err);

      const errorMessages = yupErrorMessageParse(err);

      dispatch(setState({
        invalids: Array.isArray(errorMessages) ?
          errorMessages :
          [{ message: `An Unknown Error has occured.` }],
      }));

      return false;
    }
  }

  useEffect(() => {
    dispatch(setState({
      applyLoading: adhocStatementLoading || isChargingInterest || isSendingInvoiceEmail || isIssuingLateFee || isSendingSms || isLoadingBulkActions,
    }));
  }, [adhocStatementLoading, isChargingInterest, isSendingInvoiceEmail, isIssuingLateFee, isSendingSms, isLoadingBulkActions]);

  useEffect(() => {
    dispatch(setState({
      previewLoading: previewLoadingSrc || previewFetching,
    }));
  }, [previewLoadingSrc, previewFetching]);

  useEffect(() => {
    dispatch(setState({
      preview: previewData,
      previewError: undefined,
    }));
  }, [previewData]);

  useEffect(() => {
    if (!previewApiError) {
      return;
    }

    dispatch(setState({
      previewError: getPreviewErrorMessage(previewApiError),
      preview: undefined,
    }));
  }, [previewApiError]);

  useEffect(() => {
    dispatch(setState({
      updatePolicyLoading: saveLoading,
    }));
  }, [saveLoading]);

  return {
    type,
    show,
    close,
    openSendStatement,
    openChargeInterest,
    openSendInvoiceEmail,
    openIssueLateFee,
    openSendSms,
    openBulkReminders,
    openBulkStatements,
    openBulkInterests,
    openBulkLateFees,
    openBulkAnnouncements,
    currentSystemPolicy: currentPolicy,
    currentSystemPolicyForm: currentPolicyForm,
    loading: systemPolicesLoading || updatePolicyLoading,
    applyLoading,
    previewLoading,
    update,
    saveAndExecute,
    invalids,
    result,
    resultNew,
    contactName,
    invoiceNumber,
    contactId,
    invoiceId,
    preview,
    previewError,
    onPreview,
    view,
    isBulk,
    invoiceIds,
    contactIds,
    isAnnouncement,
    contactPersonIds,
    setContactPersonIds,
    adhocContactPersons,
    adhocContactLoading,
    noContactPersonsSelected,
    showPersonSelect,
  };
}
